import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import debounce from 'lodash.debounce';
import ArrowDropDownLineIcon from 'remixicon-react/ArrowDropDownLineIcon';
import SearchLineIcon from 'remixicon-react/SearchLineIcon';
import CheckLineIcon from 'remixicon-react/CheckLineIcon';
import { LoadingSpinner } from '@/components/Icons';
import InfiniteScroll from 'react-infinite-scroll-component';
import { CoinProps } from '@/types/coins';

interface CurrencyDropdownProps {
    onSelectCurrency: (currency: CoinProps) => void;
    currentCoin: CoinProps | null;
    sourceCurrency: CoinProps | null;
    isTarget: boolean;
    usdtToTomanCurrency: CoinProps;
}

interface CurrencyDropdownListProps {
    usdtToTomanCurrency: CoinProps;
    currentSymbol?: string;
}

const CurrencyDropdownList: React.FC<CurrencyDropdownListProps> = ({
    usdtToTomanCurrency,
    currentSymbol,
}) => {
    const [currencies, setCurrencies] = useState<CoinProps[]>([]);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [page, setPage] = useState<number>(1);
    const [hasMore, setHasMore] = useState<boolean>(true);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const debouncedFetch = useMemo(() => {
        const modelsOf = (response: { data?: any }): CoinProps[] =>
            response.data?.data?.models || response.data?.models || [];
        return debounce(async (fetchPage: number, search: string) => {
            setIsLoading(true);
            try {
                const cryptoRequest = axios.get(
                    `/api/coin/currencies/?page=${fetchPage}&search=${encodeURIComponent(search)}`,
                );
                if (fetchPage === 1) {
                    // Toman, then every fiat currency and gold item (one short list each, filtered
                    // here), then the crypto pages: «یورو به تومان» was impossible before
                    const [fiat, gold, crypto] = await Promise.all([
                        axios
                            .get('/api/coin/currencies/?type=currency&page=1&limit=100')
                            .then(modelsOf)
                            .catch(() => [] as CoinProps[]),
                        axios
                            .get('/api/coin/currencies/?type=gold&page=1&limit=100')
                            .then(modelsOf)
                            .catch(() => [] as CoinProps[]),
                        cryptoRequest.then(modelsOf),
                    ]);
                    const term = search.trim().toLowerCase();
                    const matches = (coin: CoinProps) =>
                        !term ||
                        (coin.name_fa || '').includes(search.trim()) ||
                        (coin.name || '').toLowerCase().includes(term) ||
                        (coin.symbol || '').toLowerCase().includes(term);
                    const tomanAssets = [...fiat, ...gold].filter(
                        (coin) => matches(coin) && Number(coin.price) > 0,
                    );
                    setCurrencies([usdtToTomanCurrency, ...tomanAssets, ...crypto]);
                    setHasMore(crypto.length >= 10);
                } else {
                    const fetchedModels = modelsOf(await cryptoRequest);
                    setCurrencies((prev) => [...prev, ...fetchedModels]);
                    setHasMore(fetchedModels.length >= 10);
                }
            } catch (error) {
                console.error('Failed to fetch currencies:', error);
                setHasMore(false);
            } finally {
                setIsLoading(false);
            }
        }, 300);
    }, [usdtToTomanCurrency]);

    useEffect(() => {
        debouncedFetch(page, searchTerm);
        return () => {
            debouncedFetch.cancel();
        };
    }, [page, searchTerm, debouncedFetch]);

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value);
        setPage(1);
        setHasMore(true);
    };

    const fetchMoreData = () => {
        if (!isLoading && hasMore) {
            setPage((prevPage) => prevPage + 1);
        }
    };

    return (
        <div className='flex flex-col p-2 w-full'>
            <div className='relative mb-2'>
                <SearchLineIcon className='absolute right-2.5 top-1/2 -translate-y-1/2 size-4 text-gray-400 dark:text-gray-500' />
                <input
                    type='text'
                    placeholder='جستجوی نام یا نماد ارز...'
                    value={searchTerm}
                    onChange={handleSearchChange}
                    className='w-full bg-gray-100 dark:bg-white/10 text-gray-800 dark:text-white pr-8 pl-3 py-2 text-xs rounded-xl border border-transparent focus:border-blue-500 focus:outline-none transition-colors'
                    autoFocus
                />
            </div>

            <div
                id='scrollable-currencies'
                className='max-h-[220px] overflow-y-auto rounded-xl pr-0.5 scrollbar-thin scrollbar-thumb-gray-200 dark:scrollbar-thumb-white/10'
            >
                <InfiniteScroll
                    dataLength={currencies.length}
                    next={fetchMoreData}
                    hasMore={hasMore}
                    loader={
                        isLoading && (
                            <div className='py-2 text-center'>
                                <LoadingSpinner className='mx-auto size-4 animate-spin text-blue-500' />
                            </div>
                        )
                    }
                    endMessage={
                        !isLoading &&
                        currencies.length > 0 && (
                            <div className='py-2 text-center text-gray-400 text-[11px]'>
                                پایان لیست ارزها
                            </div>
                        )
                    }
                    scrollableTarget='scrollable-currencies'
                >
                    <div className='flex flex-col gap-0.5'>
                        {currencies.map((currency, index) => {
                            const isSelected = currency.symbol === currentSymbol;
                            return (
                                <ListboxOption
                                    key={`${currency.symbol}-${index}`}
                                    className={`cursor-pointer select-none relative py-2 px-2.5 text-xs rounded-xl transition-all duration-150 flex items-center justify-between ${
                                        isSelected
                                            ? 'bg-blue-50 dark:bg-blue-600/20 text-blue-600 dark:text-blue-400 font-medium'
                                            : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-white/10'
                                    }`}
                                    value={currency}
                                >
                                    <div className='flex items-center gap-2 min-w-0'>
                                        <span className='truncate font-medium'>
                                            {currency.name_fa || currency.name}
                                        </span>
                                        <span className='text-[10px] text-gray-400 dark:text-gray-500 dir-ltr'>
                                            ({currency.symbol})
                                        </span>
                                    </div>
                                    {isSelected && (
                                        <CheckLineIcon className='size-4 text-blue-600 dark:text-blue-400 shrink-0' />
                                    )}
                                </ListboxOption>
                            );
                        })}
                    </div>
                </InfiniteScroll>
            </div>
        </div>
    );
};

const CurrencyDropdown: React.FC<CurrencyDropdownProps> = ({
    onSelectCurrency,
    currentCoin,
    isTarget,
    usdtToTomanCurrency,
}) => {
    const [selectedCurrency, setSelectedCurrency] = useState<CoinProps | undefined>(
        currentCoin ?? (isTarget ? usdtToTomanCurrency : undefined),
    );

    useEffect(() => {
        if (currentCoin) {
            setSelectedCurrency(currentCoin);
        } else if (isTarget && usdtToTomanCurrency) {
            setSelectedCurrency(usdtToTomanCurrency);
        }
    }, [currentCoin, isTarget, usdtToTomanCurrency]);

    const handleSelection = (currency: CoinProps) => {
        setSelectedCurrency(currency);
        onSelectCurrency(currency);
    };

    return (
        <div className='relative shrink-0'>
            <Listbox value={selectedCurrency} onChange={handleSelection}>
                {({ open }) => (
                    <div className='relative'>
                        <ListboxButton className='flex items-center justify-between gap-1.5 px-3 py-2 bg-white dark:bg-white/10 hover:bg-gray-50 dark:hover:bg-white/15 border border-gray-200 dark:border-white/10 rounded-xl cursor-pointer text-xs font-medium text-gray-800 dark:text-white transition-all shadow-xs min-w-[130px] sm:min-w-[150px]'>
                            <div className='flex items-center gap-1.5 truncate'>
                                <span className='truncate font-semibold'>
                                    {selectedCurrency?.name_fa ||
                                        selectedCurrency?.name ||
                                        'انتخاب ارز'}
                                </span>
                                {selectedCurrency?.symbol && (
                                    <span className='text-[10px] text-gray-400 dark:text-gray-400 font-mono dir-ltr'>
                                        ({selectedCurrency.symbol})
                                    </span>
                                )}
                            </div>
                            <ArrowDropDownLineIcon
                                className={`text-gray-400 dark:text-gray-300 transition-transform duration-300 shrink-0 ${
                                    open ? 'rotate-180' : 'rotate-0'
                                }`}
                                size={18}
                            />
                        </ListboxButton>

                        <ListboxOptions className='absolute right-0 z-40 mt-2 w-[220px] sm:w-[250px] rounded-2xl bg-white dark:bg-[#1E1C3B] border border-gray-100 dark:border-white/15 shadow-xl focus:outline-none origin-top-right transition duration-200 ease-out'>
                            {open && (
                                <CurrencyDropdownList
                                    usdtToTomanCurrency={usdtToTomanCurrency}
                                    currentSymbol={selectedCurrency?.symbol}
                                />
                            )}
                        </ListboxOptions>
                    </div>
                )}
            </Listbox>
        </div>
    );
};

export default CurrencyDropdown;
